/* =========================================================================
 * Langle's closeness ladder — the single definition of how near a guess was
 * and how that gets drawn.
 *
 * Two different things are on screen per guess, and they are not the same
 * measure:
 *
 *   LADDER  the row's tint. A guess sits on the first rung whose test it
 *           passes, so the color says how near it got at its best.
 *   AXES    the four pips. Each hint is scored on its own, so the pips say
 *           which hints landed.
 *
 * These read differently on purpose. Sharing a family does not imply sharing
 * a region — Icelandic and Afrikaans are both Germanic and a hemisphere
 * apart — so a cumulative meter would light a region pip on a guess whose
 * region tag plainly says it missed. Scoring each pip separately keeps the
 * pips and the tags telling one story. The tint still survives grayscale and
 * red-green color blindness: the rightmost lit pip is the rung, since the
 * ladder tests these same axes in this same order.
 *
 * Rendered by the live board in components/Daily.js, the help legend and
 * share grid in the same file, and the past-game boards in Account.js.
 * ========================================================================= */

export const LADDER = [
  { test: (g) => g.correct,         className: 'tile-correct', label: 'Exact language — you win' },
  { test: (g) => g.sameFamily,      className: 'tile-family',  label: 'Same language family (Romance, Slavic, …)' },
  { test: (g) => g.sameMacroFamily, className: 'tile-macro',   label: 'Same broader family (Indo-European, …)' },
  { test: (g) => g.sameRegion,      className: 'tile-region',  label: 'Same geographic region' },
];

export const NO_MATCH = { className: 'tile-none', label: 'Nothing in common' };

export function rungFor(g) {
  return LADDER.find((rung) => rung.test(g)) || NO_MATCH;
}

/* The four hint axes, widest to narrowest. `short` is the letter the shared
 * text grid prints, `kind` the modifier its tiles are styled with. Exported so
 * the board meter and the share grid score a guess off one list and can't
 * disagree about what a row means. */
export const AXES = [
  { key: 'sameRegion',      short: 'R', kind: 'region', name: 'region' },
  { key: 'sameMacroFamily', short: 'M', kind: 'macro',  name: 'broader family' },
  { key: 'sameFamily',      short: 'F', kind: 'family', name: 'family' },
  { key: 'correct',         short: '✓', kind: 'exact',  name: 'exact language' },
];

export function matchedAxes(g) {
  return AXES.map((axis) => ({ ...axis, hit: Boolean(g[axis.key]) }));
}

/* One pip per hint axis, lit for the hints this guess actually landed. Gives
 * the board a non-color reading of the row for grayscale, red-green color
 * blindness, and anyone who never opened the legend. Labelled rather than
 * aria-hidden, since otherwise closeness reaches a screen reader only through
 * the tint. */
export function StepMeter({ guess }) {
  const axes = matchedAxes(guess);
  const hits = axes.filter((a) => a.hit);
  return (
    <span
      className="lng-steps"
      role="img"
      aria-label={hits.length ? `Matched: ${hits.map((a) => a.name).join(', ')}` : 'Matched nothing'}
    >
      {axes.map(({ kind, hit }) => (
        <span key={kind} className={hit ? 'is-on' : ''} />
      ))}
    </span>
  );
}

/* Whether one attribute of a guess matched the answer. A tick and a "roughly
 * equals" say what they mean on sight, which a fill color can't: the tags
 * previously leaned on solid-black-means-matched, a convention with no
 * agreed direction (Wordle's dark tile means the letter is *absent*) and one
 * that left "missed" and "broader family only" looking nearly identical. */
export const TAG_STATES = {
  on:   { className: ' is-on',   mark: '✓', hint: 'matches' },
  half: { className: ' is-half', mark: '≈', hint: 'broader family matches' },
  off:  { className: '',         mark: '',  hint: 'no match' },
};

function GuessTag({ label, state }) {
  const { className, mark, hint } = TAG_STATES[state];
  return (
    <span className={`lng-tag${className}`} title={`${label} — ${hint}`}>
      {mark && <span className="lng-tag-mark" aria-hidden="true">{mark}</span>}
      {label}
    </span>
  );
}

/* One filled board row — the guessed language, its closeness meter, and the
 * region/family tags. Shared so the live board and the account page's past
 * games stay identical; compact callers just wrap it in a different section
 * class. */
export function GuessRow({ guess }) {
  const rung = rungFor(guess);
  const familyState = guess.sameFamily ? 'on' : guess.sameMacroFamily ? 'half' : 'off';
  return (
    <div className={`lng-row is-filled ${rung.className}`}>
      <span className="lng-row-lead">
        <StepMeter guess={guess} />
        <span className="lng-row-name">{guess.guess}</span>
      </span>
      <span className="lng-row-tags">
        {guess.region && <GuessTag label={guess.region} state={guess.sameRegion ? 'on' : 'off'} />}
        {guess.family && <GuessTag label={guess.family} state={familyState} />}
      </span>
    </div>
  );
}
